import { db } from '@/firebase/init'
import { useUserStore } from '@/stores/userStore'
import { AnalysisTest } from '@team-milva/lab-models/resources'
import type { TAnalysisTest } from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { defineStore } from 'pinia'

// Read-only lab catalog data (Apps/-lab-management-/AnalysisTests) — MIL-338
// Phase 2, needed to render a real result: an AnalysisResult only carries the
// entered value (e.g. an answerID), not the display label — that comes from
// the test's own answers[]/provedTextID/unitID/etc.
export const useAnalysisTestStore = defineStore('analysisTest', {
  state: () => ({
    allAnalysisTests: [] as AnalysisTest[],
    unsubscribeAllAnalysisTests: null as (() => void) | null,
  }),

  actions: {
    getAnalysisTests(): Promise<AnalysisTest[]> {
      if (this.allAnalysisTestsLoaded) {
        return Promise.resolve(this.allAnalysisTests)
      }

      const userStore = useUserStore()

      return new Promise((resolve) => {
        if (!userStore.currentUser) {
          return resolve([])
        }

        let resolved = false
        const q = query(
          collection(db, 'Apps', '-lab-management-', 'AnalysisTests'),
          where('accountID', '==', userStore.currentUser.labAccountID)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const analysisTests: AnalysisTest[] = []
          querySnapshot.forEach((docSnap) => {
            analysisTests.push(new AnalysisTest(docSnap.data() as TAnalysisTest))
          })
          this.allAnalysisTests = analysisTests
          this.unsubscribeAllAnalysisTests = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(analysisTests)
          }
        })
      })
    },

    resetStore(): void {
      this.allAnalysisTests = []
      this.unsubscribeAllAnalysisTests?.()
      this.unsubscribeAllAnalysisTests = null
    },
  },

  getters: {
    allAnalysisTestsLoaded: (state) => !!state.unsubscribeAllAnalysisTests,

    getAnalysisTestById:
      (state) =>
      (analysisTestID: string): AnalysisTest | undefined =>
        state.allAnalysisTests.find((x) => x.id === analysisTestID),
  },
})
